import { useState, useEffect } from 'react';
import { Space, Select, DatePicker } from 'antd';
import { FilterOutlined } from '@ant-design/icons';
import type { JobStatus, TimeRange } from '../../api/types';
import { useNamespaces } from '../../api/queries/useNamespaces';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

interface EventFiltersProps {
  table: 'job_status' | 'stat' | 'log';
  status?: JobStatus;
  level?: 'DEBUG' | 'INFO' | 'WARN' | 'ERROR' | 'FATAL';
  namespace?: string;
  timeRange: TimeRange;
  customRange?: [string, string];
  onStatusChange: (status: JobStatus | undefined) => void;
  onLevelChange: (level: 'DEBUG' | 'INFO' | 'WARN' | 'ERROR' | 'FATAL' | undefined) => void;
  onNamespaceChange: (namespace: string | undefined) => void;
  onTimeRangeChange: (timeRange: TimeRange, customRange?: [string, string]) => void;
}

const JOB_STATUS_OPTIONS = [
  { value: undefined, label: 'All Statuses' },
  { value: 'JOB_START', label: 'Job Start' },
  { value: 'PROCESS_STEP_START', label: 'Step Start' },
  { value: 'PROCESS_STEP_END', label: 'Step End' },
  { value: 'JOB_END', label: 'Job End' },
  { value: 'JOB_END_EXCEPTION', label: 'Job Exception' },
];

const LOG_LEVEL_OPTIONS = [
  { value: undefined, label: 'All Levels' },
  { value: 'DEBUG', label: 'Debug' },
  { value: 'INFO', label: 'Info' },
  { value: 'WARN', label: 'Warn' },
  { value: 'ERROR', label: 'Error' },
  { value: 'FATAL', label: 'Fatal' },
];

const TIME_RANGE_OPTIONS = [
  { value: '1h', label: '1 Hour' },
  { value: '6h', label: '6 Hours' },
  { value: '24h', label: '24 Hours' },
  { value: '7d', label: '7 Days' },
  { value: 'custom', label: 'Custom Range' },
];

export function EventFilters({
  table,
  status,
  level,
  namespace,
  timeRange,
  customRange,
  onStatusChange,
  onLevelChange,
  onNamespaceChange,
  onTimeRangeChange,
}: EventFiltersProps) {
  // Track if user has clicked 'custom' but hasn't selected dates yet
  const [pendingCustom, setPendingCustom] = useState(false);
  const showCustomPicker = timeRange === 'custom' || pendingCustom;

  // Displayed value in dropdown: show 'custom' if pending or actually custom
  const displayedTimeRange = pendingCustom ? 'custom' : timeRange;

  const { data: namespacesData, isLoading: isLoadingNamespaces } = useNamespaces({
    time_range: timeRange === 'custom' ? '7d' : timeRange,
  });

  // Reset pending state when timeRange changes to custom
  useEffect(() => {
    if (timeRange === 'custom') {
      setPendingCustom(false);
    }
  }, [timeRange]);

  const namespaceOptions = [
    { value: undefined, label: 'All Namespaces' },
    ...(namespacesData?.namespaces || []).map((ns) => ({
      value: ns,
      label: ns,
    })),
  ];

  const handleTimeRangeChange = (value: TimeRange) => {
    if (value === 'custom') {
      setPendingCustom(true);
    } else {
      setPendingCustom(false);
      onTimeRangeChange(value);
    }
  };

  const handleCustomRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      const start = dates[0].toISOString();
      const end = dates[1].toISOString();
      setPendingCustom(false);
      onTimeRangeChange('custom', [start, end]);
    }
  };

  return (
    <Space wrap>
      <FilterOutlined />

      <Select
        value={displayedTimeRange}
        onChange={handleTimeRangeChange}
        options={TIME_RANGE_OPTIONS}
        style={{ width: 140 }}
      />

      {showCustomPicker && (
        <RangePicker
          showTime
          format="YYYY-MM-DD HH:mm"
          onChange={handleCustomRangeChange}
          value={customRange ? [dayjs(customRange[0]), dayjs(customRange[1])] : undefined}
          style={{ width: 340 }}
        />
      )}

      {table === 'job_status' && (
        <Select
          value={status}
          onChange={onStatusChange}
          options={JOB_STATUS_OPTIONS}
          style={{ width: 150 }}
          placeholder="Status"
          allowClear
        />
      )}

      {table === 'log' && (
        <Select
          value={level}
          onChange={onLevelChange}
          options={LOG_LEVEL_OPTIONS}
          style={{ width: 120 }}
          placeholder="Level"
          allowClear
        />
      )}

      <Select
        value={namespace}
        onChange={onNamespaceChange}
        options={namespaceOptions}
        loading={isLoadingNamespaces}
        style={{ width: 220 }}
        placeholder="Namespace"
        allowClear
        showSearch
        filterOption={(input, option) =>
          (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
        }
      />
    </Space>
  );
}
